import type { Domino, Flow } from '@shared/types/flows';
import type { FlowComposerDraft } from '@shared/types/flow-composer';
import type { ToolReadinessResult } from '@shared/types/tool-readiness';
import { evaluateToolReadiness } from '@shared/types/tool-readiness';
import type { FlowViewType } from '@shared/types/views';

export type ToolSurfaceOrigin = 'domino_preset' | 'user_created';

export interface ToolSurfaceSpec {
  id: string;
  origin: ToolSurfaceOrigin;
  title: string;
  promise: string;
  description: string;
  href: string;
  statusLabel: string;
  statusVariant: 'success' | 'warning' | 'secondary' | 'outline';
  launchLabel: string;
  viewType?: FlowViewType;
  readiness?: ToolReadinessResult;
}

const getOutputDescription = (flow: Flow): string => {
  const metricLabels = flow.output_contract?.metrics
    ?.map((metric) => metric.label)
    .filter((label) => label.trim().length > 0)
    .slice(0, 4) ?? [];

  if (metricLabels.length > 0) {
    return `Produit un résultat avec ${metricLabels.join(', ')}.`;
  }

  return flow.description || 'Produit un résultat métier réutilisable.';
};

export const flowToComposerDraft = (
  flow: Flow,
  dominos: Domino[],
): FlowComposerDraft => ({
  title: flow.title,
  description: flow.description,
  assistantSummary: flow.description || getOutputDescription(flow),
  outputDescription: getOutputDescription(flow),
  viewType: flow.view_type,
  toolSurface: flow.tool_surface ?? undefined,
  outputContract: flow.output_contract,
  personaKey: flow.persona_key,
  trigger: 'manual',
  inputs: flow.inputs,
  dominos: dominos
    .slice()
    .sort((left, right) => left.position - right.position)
    .map((domino) => ({
      label: domino.label,
      actionType: domino.action_type,
      config: domino.config,
      inputMapping: domino.input_mapping,
      hitlBehavior: domino.hitl_behavior,
      contextCategories: domino.context_categories,
    })),
});

export const getFlowToolReadiness = (
  flow: Flow,
  dominos: Domino[],
): ToolReadinessResult => evaluateToolReadiness(flowToComposerDraft(flow, dominos));

export const getFlowToolSurface = (
  flow: Flow,
  dominos: Domino[] = [],
): ToolSurfaceSpec => {
  const readiness = getFlowToolReadiness(flow, dominos);
  const isPublished = flow.status === 'active';
  const publishableDraft = flow.status === 'draft' && readiness.publishable;

  return {
    id: flow.id,
    origin: 'user_created',
    title: flow.title,
    promise: getOutputDescription(flow),
    description: flow.description || 'Tool créé dans Domino.',
    href: `/tools/${flow.id}`,
    statusLabel: isPublished
      ? 'Publié'
      : publishableDraft
        ? 'Prêt à publier'
        : 'Brouillon',
    statusVariant: isPublished
      ? 'success'
      : publishableDraft
        ? 'warning'
        : 'outline',
    launchLabel: 'Lancer le tool',
    viewType: flow.view_type,
    readiness,
  };
};
